import { useEffect, useState, useMemo } from 'react';
import { useInventoryAdjustmentQuery } from '../../../hooks/inventory/useInventoryAdjustmentQuery';
import { useBranch } from '../../../hooks/branches/useBranch';
import { useProduct } from '../../../hooks/useProduct';
import AdvancedRecordTable from '../../Tables/advancedRecord/AdvancedRecordTable';
import { FilterConfig } from '../../Tables/advancedRecord/FilterBar';
import { InventoryAdjustmentTableDTO } from '../../../types/inventory/adjustment/InventoryAdjustmentTypes';
import { inventoryAdjustmentColumns } from '../../../features/inventory/adjustment/inventoryAdjustmentColumns';
import { buildInventoryAdjustmentFilters } from '../../../features/inventory/adjustment/buildInventoryAdjustmentFilters';
import { adjustmentTypeOptions } from '../../../constants/inventory/adjustment/adjustmentTypeOptions';
import CreateInventoryAdjustmentModal from './Modals/CreateInventoryAdjustmentModal';
import InventoryAdjustmentPendingModal from './Modals/InventoryAdjustmentPendingModal';
import { useInventoryAdjustment } from '../../../hooks/inventory/useInventoryAdjustment';
import InventoryAdjustmentDetailModal from './Modals/InventoryAdjustmentDetailModal';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';
import useConfirmModal from '../../../hooks/useConfirmModal';

const InventoryAdjustmentTableSection: React.FC = () => {
  const {
    list,
    paginator,
    handlerGetFilteredAdjustments,
    handlerClearAdjustments,
    showAdjustmentModal,
    showPendingAdjustmentModal,
    showAdjustmentDetailModal,
    handlerOpenAdjustmentModal,
    handlerCloseAdjustmentModal,
    handlerOpenPendingAdjustmentModal,
    handlerClosePendingAdjustmentModal,
    handlerCloseAdjustmentDetailModal,
    pendingCount,
    handlerGetPendingAdjustmentsCount,
  } = useInventoryAdjustmentQuery();

  const { hasRole } = useAuthUser();
  const confirm = useConfirmModal();

  const {
    getById,
    approve,
    reject,
    cancel,
    revert,
  } = useInventoryAdjustment();

  const { fetchBranchDropdown, branchDropdown } = useBranch();
  const { handlerGetProductsForDropdown, productsDropdown } = useProduct();
  const [filters, setFilters] = useState({});
  const [filtersApplied, setFiltersApplied] = useState(false);
  const pageSize = paginator?.size ?? 10;
  const currentPage = paginator?.number ?? 0;

  useEffect(() => {
    fetchBranchDropdown();
    handlerGetProductsForDropdown();
    handlerGetPendingAdjustmentsCount();
  }, []);

  const columns = useMemo(() => inventoryAdjustmentColumns, []);

  const filterFields: FilterConfig[] = useMemo(
    () =>
      buildInventoryAdjustmentFilters(
        branchDropdown,
        productsDropdown,
        adjustmentTypeOptions
      ),
    [branchDropdown, productsDropdown]
  );

  const buildFilterPayload = (rawFilters: any) => {
    const payload: any = {};

    if (rawFilters.branchId) payload.branchId = Number(rawFilters.branchId);
    if (rawFilters.productId) payload.productId = Number(rawFilters.productId);
    if (rawFilters.adjustmentType)
      payload.adjustmentType = rawFilters.adjustmentType;

    if (rawFilters.dateFrom)
      payload.dateFrom = `${rawFilters.dateFrom}T00:00:00`;

    if (rawFilters.dateTo) payload.dateTo = `${rawFilters.dateTo}T23:59:59`;

    return payload;
  };

  const handleFilterChange = (newFilters: any) => {
    const payload = buildFilterPayload(newFilters);
    setFilters(payload);
    setFiltersApplied(true);
    handlerGetFilteredAdjustments(payload, 0, pageSize);
  };

  useEffect(() => {
    if (filtersApplied) {
      handlerGetFilteredAdjustments(filters, currentPage, pageSize);
    }
  }, [currentPage]);

  const confirmAction = (opts: {
    title: string;
    message: string;
    confirmText: string;
    onConfirm: () => void;
  }) => {
    confirm({
      title: opts.title,
      message: opts.message,
      icon: 'warning',
      confirmText: opts.confirmText,
      cancelText: 'Volver',
      onConfirm: opts.onConfirm,
    });
  };

  const getActions = (row: InventoryAdjustmentTableDTO) => {
    const actions: any[] = [];

    // 👁 Todos pueden ver detalle
    actions.push({
      key: 'view',
      label: 'Ver detalle',
      onClick: () => getById(row.id),
    });

    // ❌ Cancelar: permitido para cualquier usuario
    if (row.status === 'Creado' || row.status === 'Finalizado') {
      actions.push({
        key: 'cancel',
        label: 'Cancelar',
        onClick: () =>
          confirmAction({
            title: '¿Anular este ajuste?',
            message:
              'Se cancelará el ajuste completo. Esta acción no se puede deshacer.',
            confirmText: 'Anular ajuste',
            onConfirm: () => cancel(row.id),
          }),
      });
    }

    const isAdminOrSupport = hasRole('ADMIN') || hasRole('SUPPORT');

    // ==========================================
    // 🛡 ADMIN / SUPPORT
    // ==========================================
    if (isAdminOrSupport) {
      switch (row.status) {
        case 'Finalizado':
          actions.push(
            {
              key: 'approve',
              label: 'Aprobar',
              onClick: () =>
                confirmAction({
                  title: '¿Aprobar este ajuste?',
                  message:
                    'Al aprobarlo se APLICARÁN los cambios de inventario de inmediato.',
                  confirmText: 'Aprobar',
                  onConfirm: () => approve(row.id),
                }),
            },
            {
              key: 'reject',
              label: 'Rechazar',
              onClick: () =>
                confirmAction({
                  title: '¿Rechazar este ajuste?',
                  message: 'El ajuste quedará rechazado y no se aplicará al inventario.',
                  confirmText: 'Rechazar',
                  onConfirm: () => reject(row.id),
                }),
            }
          );
          break;

        case 'Aprobado':
          actions.push({
            key: 'revert',
            label: 'Revertir',
            onClick: () =>
              confirmAction({
                title: '¿Revertir este ajuste?',
                message:
                  'Se deshará el cambio de inventario que este ajuste ya aplicó.',
                confirmText: 'Revertir',
                onConfirm: () => revert(row.id),
              }),
          });
          break;

        default:
          // Creado / Rechazado / Cancelado / Revertido
          break;
      }
    }

    return actions;
  };

  const topLeftActions = [
    {
      label: 'Ajustes pendientes',
      count: pendingCount,
      onClick: () => handlerOpenPendingAdjustmentModal(),
    },
  ];

  return (
    <>
      <AdvancedRecordTable
        columns={columns}
        data={list}
        paginator={paginator}
        pageSize={pageSize}
        onPageChange={(page) =>
          handlerGetFilteredAdjustments(filters, page, pageSize)
        }
        onPageSizeChange={(size) =>
          handlerGetFilteredAdjustments(filters, 0, size)
        }
        filtersConfig={filterFields}
        onFilterChange={handleFilterChange}
        onResetFilters={handlerClearAdjustments}
        url="/inventory/adjustments"
        noDataMessage="No hay ajustes registrados."
        getActions={getActions}
        topRightAction={{
          label: 'Nuevo Ajuste',
          onClick: handlerOpenAdjustmentModal,
        }}
        topLeftActions={topLeftActions}
      />

      <CreateInventoryAdjustmentModal
        isOpen={showAdjustmentModal}
        onClose={handlerCloseAdjustmentModal}
      />

      <InventoryAdjustmentPendingModal
        isOpen={showPendingAdjustmentModal}
        onClose={handlerClosePendingAdjustmentModal}
      />

      <InventoryAdjustmentDetailModal
        isOpen={showAdjustmentDetailModal}
        onClose={handlerCloseAdjustmentDetailModal}
      />
    </>
  );
};

export default InventoryAdjustmentTableSection;
